import React, { useState, useEffect, useRef } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import api from '../services/api'
import EmployeeAvatar from './EmployeeAvatar'
import Icon from './Icon'
import './UserMenu.css'

function UserMenu({ sidebarCollapsed = false }) {
  const { user, logout } = useAuth()
  const navigate = useNavigate()
  const [isOpen, setIsOpen] = useState(false)
  const [employee, setEmployee] = useState(null)
  const menuRef = useRef(null)
  const buttonRef = useRef(null)

  useEffect(() => {
    if (user?.employee_id) {
      fetchEmployeeData()
    }
  }, [user])

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        isOpen &&
        menuRef.current &&
        buttonRef.current &&
        !menuRef.current.contains(event.target) &&
        !buttonRef.current.contains(event.target)
      ) {
        setIsOpen(false)
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen])

  const fetchEmployeeData = async () => {
    if (user?.employee_id) {
      try {
        const response = await api.get(`/company/team/${user.employee_id}`)
        if (response.data && response.data.employee) {
          setEmployee(response.data.employee)
        }
      } catch (error) {
        console.error('Error fetching employee data:', error)
      }
    }
  }

  const handleToggle = () => {
    setIsOpen(!isOpen)
  }

  const handleLogout = () => {
    setIsOpen(false)
    logout()
    navigate('/login')
  }

  const handleMenuItemClick = () => {
    setIsOpen(false)
  }

  const getRoleLabel = (role) => {
    switch (role) {
      case 'admin':
        return 'Администратор'
      case 'management':
        return 'Руководство'
      case 'department_head':
        return 'Руководитель'
      case 'accountant':
        return 'Бухгалтер'
      default:
        return 'Сотрудник'
    }
  }

  // Создаем объект employee для EmployeeAvatar, если есть данные
  const avatarEmployee = employee || (user?.avatar_url ? {
    full_name: user.login,
    avatar_url: user.avatar_url,
    user: { avatar_url: user.avatar_url }
  } : {
    full_name: user?.login || 'Пользователь'
  })

  // Определяем имя для отображения: если есть связанный сотрудник - показываем его имя, иначе логин
  const displayName = employee?.full_name || user?.login || 'Пользователь'

  return (
    <div className="user-menu-container">
      <button
        ref={buttonRef}
        className="user-menu-button"
        onClick={handleToggle}
        aria-expanded={isOpen}
        aria-haspopup="true"
        title={sidebarCollapsed ? `${displayName} · ${getRoleLabel(user?.role)}` : undefined}
      >
        <div className="user-menu-avatar">
          <EmployeeAvatar
            employee={avatarEmployee}
            size="small"
            showStatus={false}
          />
        </div>
        <div className="user-menu-info">
          <span className="user-menu-name">{displayName}</span>
          <span className="user-menu-role">{getRoleLabel(user?.role)}</span>
        </div>
        <Icon 
          name={isOpen ? "chevronUp" : "chevronDown"} 
          size={16} 
          className="user-menu-chevron"
        />
      </button>

      {isOpen && (
        <div ref={menuRef} className="user-menu-dropdown">
          <div 
            className={`user-menu-header ${employee ? 'user-menu-header-clickable' : ''}`}
            onClick={() => {
              if (employee?.full_name) {
                navigate(`/company/team/${encodeURIComponent(employee.id || employee.full_name)}`)
                setIsOpen(false)
              }
            }}
          >
            <div className="user-menu-header-avatar">
              <EmployeeAvatar
                employee={avatarEmployee}
                size="medium"
                showStatus={false}
              />
            </div>
            <div className="user-menu-header-info">
              <div className="user-menu-header-name">{displayName}</div>
              <div className="user-menu-header-role">{getRoleLabel(user?.role)}</div>
              {employee && employee.full_name !== displayName && (
                <div className="user-menu-header-employee">{user?.login}</div>
              )}
            </div>
          </div>
          <div className="user-menu-divider"></div>
          <Link
            to="/settings"
            className="user-menu-item"
            onClick={handleMenuItemClick}
          >
            <Icon name="settings" size={18} />
            <span>Настройки</span>
          </Link>
          <button
            className="user-menu-item user-menu-item-logout"
            onClick={handleLogout}
          >
            <Icon name="exit" size={18} />
            <span>Выход</span>
          </button>
        </div>
      )}
    </div>
  )
}

export default UserMenu

